import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { MediaGallery } from '@theme/media-gallery';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';
import { getCheckedPurchaseOption, restorePurchaseOption } from '@theme/purchase-options';
import { productHandleFromUrl } from '@theme/utilities';
import { MATCHING_SET_PROPERTIES, getSubscriptionBoxProperties } from '@theme/line-item-properties';
import { promoCoversProduct } from '@theme/utm-price';
import { formatMoney } from '@theme/money-formatting';
import { buildSearchUrl, findVariantBySize } from '@theme/ss-utils';

/**
 * Product builder — the wizard around one or more `product-builder-selection` blocks.
 *
 * It owns nothing a shopper picks. A selection owns its own style and size, a `tab-group`
 * owns which category is showing, and this coordinates them: which step is open, whether it
 * can be left, which selection the print step is building for, the url, and full screen.
 *
 * Steps are theme blocks, so the step count comes from what a merchant placed. A selection
 * may hold one step or two, and more than one selection is what the shopper calls a pair.
 *
 * The url carries every selection — `?selection=2&style=boxers,briefs&size=m,s&step=print`
 * — one slot per selection in order, with `selection` naming the one in play and omitted
 * when there is only one. Handleized labels throughout, never ids or indexes.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} stepPanel - Step panels
 * @property {HTMLElement[]} stepTitle - Step titles, one per step that renders one
 * @property {HTMLElement} stepper - Row the progress buttons are gathered into
 * @property {HTMLElement[]} step - Progress-indicator step wrappers
 * @property {HTMLElement[]} stepCircle - Progress-indicator circles
 * @property {HTMLButtonElement} prevArrow - Previous-step arrow
 * @property {HTMLButtonElement} nextArrow - Next-step arrow
 * @property {HTMLElement[]} [printSubtitle] - Print step subheading, rewritten per selection
 * @property {HTMLElement} [printTarget] - Where the fetched print is rendered
 * @property {HTMLElement} [switcher] - Row that switches which selection the print step dresses
 * @property {HTMLButtonElement} [fullScreenClose] - Close button, rendered only when full screen is enabled
 *
 * @property {HTMLButtonElement[]} [stepContinue] - Each step's continue button
 *
 * @extends {Component<Refs>}
 */
class ProductBuilder extends Component {
  /** @type {number} 1-based current step */
  currentStep = 1;

  /** @type {number} Total number of steps */
  maxStep = 1;

  /**
   * @type {number} Which selection the print step is building for, as an index into
   * `selections`. Only meaningful once the selection steps are behind us.
   */
  activeSelection = 0;

  /** @returns {any[]} Every selection, in the order they are stepped through. */
  get selections() {
    return /** @type {any[]} */ ([...this.querySelectorAll('product-builder-selection')]);
  }

  /**
   * @returns {any} The selection everything else reads: the one being edited, or the one
   * the print step is building for once the steps are behind us.
   */
  get selection() {
    const editing = this.#findSelection(this.#panel(this.currentStep));

    return editing ?? this.selections[this.activeSelection] ?? null;
  }

  /** @type {HTMLElement | null} The style of whichever selection is in play. */
  get selectedIcon() {
    return this.selection?.style ?? null;
  }

  /** @type {string | null} The size of whichever selection is in play. */
  get selectedSize() {
    return this.selection?.size ?? this.#printSize;
  }

  /**
   * @type {string | null} The size picked in the print step.
   *
   * A flow with no size step of its own asks for it there, so this is the only place it
   * is recorded. Named by the option, which is what the picker names its inputs after.
   */
  get #printSize() {
    for (const input of this.#printSizeInputs()) {
      if (input.checked) return input.value;
    }

    return null;
  }

  /** @returns {HTMLInputElement[]} The print step's size choices, if it offers any. */
  #printSizeInputs() {
    const inputs =
      this.refs.printTarget?.querySelectorAll('variant-picker input[type="radio"][data-option-value-id]') ?? [];

    return [...inputs].filter((input) => /size/i.test(/** @type {HTMLInputElement} */ (input).name));
  }

  /**
   * @type {Array<string | null>} The print each selection is wearing, by its index.
   *
   * Held here rather than on the selections: the print step belongs to no selection, and
   * the chips above it are what say which one is being dressed.
   */
  #prints = [];

  /** @type {string | null} The print group the shopper last had open, across a re-render. */
  #printGroup = null;

  /** @type {string | null} That group's Shopify tag, for ranking the style's first print. */
  #printGroupTag = null;

  /** @type {string | null} The print the selection in play is wearing. */
  get selectedPrint() {
    return this.#prints[this.#selectionIndex] ?? null;
  }

  set selectedPrint(handle) {
    this.#prints[this.#selectionIndex] = handle;
  }

  /** @returns {number} Which selection is in play, as an index. */
  get #selectionIndex() {
    const index = this.selections.indexOf(this.selection);

    return index >= 0 ? index : this.activeSelection;
  }

  /** @type {number | null} Step to return to once an edit from a chip is done */
  #returnStep = null;

  /** @type {Map<string, string>} Fetched print markup, keyed by product url + size */
  #printCache = new Map();

  /** @type {Map<string, any>} Product JSON by path, for resolving a size to a variant */
  #productCache = new Map();

  /**
   * @type {string | null} The print template's section id, learned from the first
   * response. Dynamic ids embed a number that changes whenever the template is
   * recreated, so it is discovered rather than configured.
   */
  #printSectionId = null;

  /** @returns {string} Where the learned section id is kept between page loads. */
  get #printSectionKey() {
    return `product-builder:print-section:${this.dataset.printTemplate ?? ''}`;
  }

  /** @type {AbortController | undefined} */
  #printAbortController;

  /** @type {((event: Event) => void) | undefined} */
  #handleBuilderStart;

  /** @type {((event: Event) => void) | undefined} */
  #handleSectionSelect;

  /** @type {((event: KeyboardEvent) => void) | undefined} */
  #handleFullScreenEscape;

  /** @type {HTMLElement | null} Focus returns here when full screen closes. */
  #focusBeforeFullScreen = null;

  /** @type {Element[]} Siblings this component made inert, so only those are restored. */
  #inertedSiblings = [];

  /** @type {boolean} Whether the builder was hidden before it went full screen. */
  #wasHiddenBeforeFullScreen = false;

  /** @type {boolean} True once the scroll lock, inert siblings and focus move are applied. */
  #fullScreenActive = false;

  connectedCallback() {
    super.connectedCallback();

    this.#gatherStepper();
    this.#gatherCategories();
    this.#numberSteps();

    this.maxStep = (this.refs.stepPanel ?? []).length || 1;
    this.currentStep = parseInt(this.dataset.currentStep ?? '1', 10) || 1;

    // Before anything is awaited. Every panel is on the page — the markup cannot know which
    // step is open — and the full state below waits on the selection components being
    // defined, which is an await, which is a paint: long enough to see the whole flow
    // stacked. This much needs nothing but the panels themselves.
    this.#openStepPanel();

    // A pick, not the selector's first render: every fetched hero brings a new selector,
    // whose init would fetch another hero and never settle. The selector announces its
    // pick rather than re-rendering a section, so cancelling stops its own re-render.
    this.addEventListener('ss-print-swatches:update', this.#handlePrintPick);

    // The print the row opens on is announced once, and a flow arriving here with nothing in
    // the url has picked nothing — so that first print is what dresses the step.
    this.addEventListener('ss-print-swatches:init', this.#handlePrintPick);

    this.addEventListener('product-builder-selection:change', this.#handleSelectionChange);

    // The form below knows one product; a matching set is two, on two plans.
    this.addEventListener('product-form:lines', this.#handleCartLines);

    this.#restoreUrlWhenReady();

    this.activeSelection = 0;

    // A section that follows this one connects later — a custom element upgrades whenever
    // its module arrives — so the announcement made while applying the initial selection
    // reached nobody. Rather than guess at a delay, answer whoever asks.
    document.addEventListener('features:request', this.#announceFeatures);

    // The config rows arrive with every fetched print, so they ask rather than be told.
    this.addEventListener('product-builder-accordion:request', this.#answerConfigRequest);
    // A size change re-renders the product, morphing everyone who asked back to the markup
    // Liquid ships. The print is unchanged, so nothing else puts them right.
    document.addEventListener(ThemeEvents.variantUpdate, this.#reanswerAfterVariant);
    this.addEventListener('product-builder:cross-sell-request', this.#answerCrossSell);
    this.addEventListener('product-builder:cross-sell-choose', this.#takeCrossSell);
    this.addEventListener('product-builder-accordion:confirm', this.#handleConfigConfirm);

    // Read the emitter from the event: more than one section can start the builder.
    if (this.dataset.hideUntilStarted !== undefined) {
      this.#handleBuilderStart = (event) => {
        // Full screen covers the page, so the flow below it is left exactly as it was.
        if (this.dataset.fullScreen === undefined) {
          this.removeAttribute('hidden');
          const emitterSection = this.#outermostSection(event.target);
          if (emitterSection && emitterSection !== this.closest('.shopify-section')) {
            emitterSection.hidden = true;
          }
        }
        // A style named by the event has already made the style step's choice, so the
        // builder opens on the size step. An event carrying none resumes the step the
        // shopper closed on.
        if (this.#preselectStyle(/** @type {CustomEvent} */ (event).detail)) {
          this.goToStep(this.#stepAsking('product-builder-sizes') || this.currentStep);
        }

        this.#openFullScreen();
      };
      document.addEventListener('builder:start', this.#handleBuilderStart);
    }

    // In the theme editor the section is hidden — reveal it when the merchant clicks it.
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.#handleSectionSelect = (event) => {
        if (event.detail?.sectionId === this.closest('.shopify-section')?.id?.replace('shopify-section-', '')) {
          this.removeAttribute('hidden');
        }
      };
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
    }

    if (this.dataset.fullScreenOnLoad !== undefined) this.#openFullScreen();

    this.updateUI();

    // CSS opened the first step and tab for the first paint; [hidden] rules from here.
    this.setAttribute('hydrated', '');
  }

  /**
   * Cover the viewport and lock the page behind. Escape is bound at the document
   * rather than the component, so it still closes when focus has drifted out.
   */
  #openFullScreen() {
    if (this.dataset.fullScreen === undefined || this.#fullScreenActive) return;
    this.#fullScreenActive = true;

    this.#focusBeforeFullScreen = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Visible only while full screen, or closing leaves the builder sitting in the page.
    this.#wasHiddenBeforeFullScreen = this.hasAttribute('hidden') || this.dataset.hideUntilStarted !== undefined;
    this.removeAttribute('hidden');
    this.setAttribute('full-screen', '');
    document.documentElement.setAttribute('scroll-lock', '');

    // Covering the viewport makes this a modal, so honour the contract: `inert` takes
    // the rest of the page out of the tab order and the accessibility tree together.
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    const label = this.dataset.fullScreenLabel;
    if (label) this.setAttribute('aria-label', label);

    // Each ancestor's other children, not just the body's: <main> contains the builder
    // so it stays live, and its sibling sections would otherwise remain tabbable.
    this.#inertedSiblings = [];
    for (let node = this; node && node !== document.body; node = node.parentElement) {
      for (const sibling of node.parentElement?.children ?? []) {
        if (sibling === node || sibling.hasAttribute('inert')) continue;
        sibling.setAttribute('inert', '');
        this.#inertedSiblings.push(sibling);
      }
    }

    // Only what is on the page now: a popup mounted later paints above the builder,
    // and inerting it would leave the shopper unable to dismiss it.
    this.#handleFullScreenEscape = (event) => {
      if (event.key !== 'Escape') return;

      // A dialog above the builder — the media zoom, the size guide — owns Escape, and
      // native dialogs handle it themselves. Closing the builder out from under one
      // would take the shopper two steps back for one press.
      if (document.querySelector('dialog[open]')) return;

      this.closeFullScreen();
    };
    document.addEventListener('keydown', this.#handleFullScreenEscape);

    // Focus the dialog itself, not the close button: a programmatic focus() on a
    // button matches :focus-visible, so the shopper saw a focus ring on page load.
    // Tab still reaches the close button first.
    this.setAttribute('tabindex', '-1');
    this.focus();
  }

  /** Called by the close button, and by Escape. */
  closeFullScreen() {
    if (!this.#fullScreenActive) return;
    this.#fullScreenActive = false;

    this.removeAttribute('full-screen');
    if (this.#wasHiddenBeforeFullScreen) this.setAttribute('hidden', '');
    this.#wasHiddenBeforeFullScreen = false;
    document.documentElement.removeAttribute('scroll-lock');

    this.removeAttribute('role');
    this.removeAttribute('aria-modal');
    this.removeAttribute('aria-label');
    this.removeAttribute('tabindex');
    for (const node of this.#inertedSiblings) node.removeAttribute('inert');
    this.#inertedSiblings = [];

    if (this.#handleFullScreenEscape) {
      document.removeEventListener('keydown', this.#handleFullScreenEscape);
      this.#handleFullScreenEscape = undefined;
    }

    this.#focusBeforeFullScreen?.focus();
    this.#focusBeforeFullScreen = null;
  }

  /**
   * Move each step's progress button into the stepper.
   *
   * A block renders its button and its panel together, so the buttons arrive scattered
   * among the panels. The section paints a skeleton row in their place until this runs.
   */
  #gatherStepper() {
    const stepper = this.refs.stepper;
    const next = this.refs.nextArrow;
    if (!stepper || !next) return;

    for (const step of this.refs.step ?? []) stepper.insertBefore(step, next);
  }

  /**
   * Move the category style grids into the Style step. Their tabs render them in the
   * Category step, which owns the categories — a block cannot render into two places.
   */
  #gatherCategories() {
    const target = this.refs.stylePanelTarget;
    const tiles = this.refs.categoryTile ?? [];
    if (!tiles.length) return;

    if (target) {
      for (const tile of tiles) {
        const panel = document.getElementById(tile.getAttribute('aria-controls') ?? '');
        if (panel) target.appendChild(panel);
      }

      // The grids carry refs addressed to the selection they just arrived in, and they
      // arrived from outside it — which its observer reads as somebody else's business.
      target.closest('product-builder-selection')?.updatedCallback?.();
    }

    this.#countCategoryStyles();
  }

  /**
   * Label each tile with how many styles its category offers. Counted from the panel:
   * the icon list is on a nested block, which reaches Liquid as a stub.
   */
  #countCategoryStyles() {
    for (const tile of this.refs.categoryTile ?? []) {
      const panel = document.getElementById(tile.getAttribute('aria-controls') ?? '');
      const count = panel?.querySelectorAll('.product-builder__icon-item').length ?? 0;
      const label = tile.querySelector('.product-builder__category-count');
      if (!label) continue;

      const template = count === 1 ? tile.dataset.countOne : tile.dataset.countOther;
      const text = count && template ? template.replace('[count]', String(count)) : '';

      label.textContent = text;
      tile.dataset.count = text;
    }
  }

  /** @returns {HTMLElement | null} The tile whose category is picked, if any. */
  get activeCategoryTile() {
    return (this.refs.categoryTile ?? []).find((tile) => tile.getAttribute('aria-pressed') === 'true') ?? null;
  }

  /**
   * Called by the on:click binding on each category tile. Shows that category's panel
   * itself rather than going through tab-group, whose own panel list no longer holds
   * them once they have been moved out of it.
   *
   * @param {MouseEvent} event
   */
  selectCategory(event) {
    const tile = /** @type {HTMLElement | null} */ (event.target)?.closest?.('.product-builder__category-tile');
    if (!(tile instanceof HTMLElement)) return;

    this.#pickCategory(tile);
    this.updateUI();
  }

  /**
   * Mark a category picked and show its styles.
   * @param {HTMLElement} tile
   */
  /** Name the category in play on the print step, for anything staging what it holds. */
  #publishCategory() {
    const host = this.refs.printTarget;
    if (!host) return;

    const value = this.activeCategoryTile?.getAttribute('aria-controls') ?? '';

    if (value) host.dataset.category = value;
    else delete host.dataset.category;
  }

  #pickCategory(tile) {
    for (const other of this.refs.categoryTile ?? []) {
      const picked = other === tile;
      // `is-selected` draws the ring, as on a style tile; aria-pressed carries the meaning.
      other.setAttribute('aria-pressed', String(picked));
      other.classList.toggle('is-selected', picked);

      const panel = document.getElementById(other.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !picked;

      // No style is picked yet, so the reviews and features answer for the one this
      // category opens on.
      if (picked) {
        const firstIcon = /** @type {HTMLElement | null} */ (panel?.querySelector('.product-builder__icon-item'));
        this.#syncReviewsProduct(firstIcon?.dataset.reviewsProductId);
        this.#updateFeatures(false, firstIcon?.dataset.productUrl ?? null);
      }
    }

    this.#publishCategory();
  }

  /**
   * Pick the category the url names, or the one holding a restored style.
   *
   * `?category=` is how a link opens the flow on a category outright — a nav or a campaign
   * pointing at Socks, which then lands on the style step. A PDP's link names only a style,
   * so the panel holding it says which category that was. With neither, the walk below stops
   * on the category step.
   *
   * @param {string | null} handle The handleized category label from the url.
   */
  #restoreCategory(handle) {
    const tiles = this.refs.categoryTile ?? [];
    if (!tiles.length || this.activeCategoryTile) return;

    const named = handle ? tiles.find((tile) => tile.dataset.categoryHandle === handle) : null;

    // `selection.style` is the icon that was picked, so the panel holding it names the
    // category without any matching on handles.
    const picked = this.selections.map((selection) => selection.style).find(Boolean);
    const panelId = picked?.closest('.product-builder__tab-panel')?.id;
    const derived = panelId ? tiles.find((tile) => tile.getAttribute('aria-controls') === panelId) : null;

    const tile = named ?? derived;
    if (tile) this.#pickCategory(tile);
  }

  /**
   * Stamp each step and its panel with its position. Steps are theme blocks, and
   * `forloop` does not exist inside `content_for 'blocks'`, so the order they were
   * rendered in is the only thing that says which step is which.
   */
  #numberSteps() {
    (this.refs.step ?? []).forEach((step, i) => {
      step.dataset.step = String(i + 1);
      const circle = step.querySelector('.product-builder__step-circle');
      if (circle instanceof HTMLElement) circle.dataset.step = String(i + 1);
    });
    (this.refs.stepPanel ?? []).forEach((panel, i) => {
      panel.dataset.step = String(i + 1);
    });
  }

  /**
   * The outermost `.shopify-section` above a node — `closest` stops at the nearest.
   * @param {EventTarget | null} node
   * @returns {HTMLElement | null}
   */
  #outermostSection(node) {
    let element = node instanceof Element ? node : null;
    /** @type {HTMLElement | null} */
    let outermost = null;
    while (element) {
      const section = /** @type {HTMLElement | null} */ (element.closest('.shopify-section'));
      if (!section) break;
      outermost = section;
      element = section.parentElement;
    }
    return outermost;
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('features:request', this.#announceFeatures);
    this.removeEventListener('product-builder-accordion:request', this.#answerConfigRequest);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#reanswerAfterVariant);
    this.removeEventListener('product-builder:cross-sell-request', this.#answerCrossSell);
    this.removeEventListener('product-builder:cross-sell-choose', this.#takeCrossSell);
    this.removeEventListener('product-builder-accordion:confirm', this.#handleConfigConfirm);
    if (this.#handleBuilderStart) {
      document.removeEventListener('builder:start', this.#handleBuilderStart);
    }
    if (this.#handleSectionSelect) {
      document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    }
    this.#printAbortController?.abort();
    // Leaving the page scroll-locked would strand every other section.
    this.closeFullScreen();
  }

  /**
   * Build one chip per pair, and mark which pair the print step is building for.
   *
   * Cloned rather than rendered by Liquid because the block holding them cannot see the
   * step blocks beside it, so it cannot know how many pairs a flow has.
   */
  #renderSwitcher() {
    const row = this.refs.switcher;
    if (!row) return;

    const selections = this.selections;
    // One selection is the single-pack flow, where this would only say what the step above
    // already says.
    row.hidden = selections.length < 2;
    if (row.hidden) return;

    const sizeText = row.dataset.sizeText ?? '[size]';

    this.render(
      {
        options: selections.map((selection, index) => ({
          index: String(index),
          // Where Change sends the shopper: the first step this selection owns.
          step: String(this.#findSteps(selection)[0] ?? 0),
          current: index === this.activeSelection ? 'true' : 'false',
          // The whole circle, not an `svg` or `img` inside it: the art is a CSS mask on a span.
          icon: selection.style?.querySelector('.product-builder__icon-img')?.outerHTML ?? '',
          style: selection.style?.dataset.label ?? this.#stepLabel(this.#findSteps(selection)[0] ?? 0),
          size: selection.size ? sizeText.replace('[size]', selection.size) : '',
          // The covering button carries no text of its own, so its name has to say both the
          // pair it acts on and what it is showing for that pair.
          label: [selection.style?.dataset.label, selection.size].filter(Boolean).join(', '),
          // Named per pair rather than a bare "Change", which says nothing about which of
          // the two it would take the shopper back to.
          changeLabel: [
            (row.dataset.changeText ?? 'Change').replace('[pair]', this.#stepLabel(this.#findSteps(selection)[0] ?? 0)),
            [selection.style?.dataset.label, selection.size].filter(Boolean).join(', '),
          ]
            .filter(Boolean)
            .join(': '),
        })),
      },
      row,
    );
  }

  /**
   * @param {number} step
   * @returns {string} The step's own label, for a pair with nothing picked yet.
   */
  #stepLabel(step) {
    return (
      (this.refs.step ?? []).find((el) => parseInt(el.dataset.step ?? '0', 10) === step)?.textContent?.trim() ?? ''
    );
  }

  /**
   * Tell the print row which pairs it is answering for.
   *
   * A matching set is only offered prints every pair can wear, so the row is given all of
   * them and asks for each in turn. One selection is the single-pack flow, where the row
   * answers for the product on screen and needs telling nothing.
   *
   * Only a flow that says it is a matching set: a pack of two picked independently wants the
   * prints made for the pair on screen, not the ones both could wear.
   */
  #publishSelections() {
    if (!('matchingPrints' in this.dataset)) return;

    const row = this.querySelector('ss-print-swatches');
    if (!row) return;

    const selections = this.selections
      .map((/** @type {any} */ selection) => ({
        productType: selection.style?.dataset.productTypeConfig ?? '',
        size: selection.size ?? '',
      }))
      .filter((entry) => entry.productType && entry.size);

    if (selections.length < 2 || selections.length < this.selections.length) return;

    const wanted = JSON.stringify(selections);
    if (row.dataset.matching === wanted) return;

    row.dataset.matching = wanted;
    /** @type {any} */ (row).pending = /** @type {any} */ (row).fetchData();
  }

  /**
   * @returns {boolean} Whether the pairs are bought as a matching set: every one of them in
   *   the same print, which the print selector answers for all of them at once.
   */
  get #isMatchingSet() {
    return 'matchingPrints' in this.dataset && this.selections.length > 1;
  }

  /** @returns {any[]} The galleries this drives, which a section render then leaves alone. */
  get #galleries() {
    if (!this.#isMatchingSet) return [];

    const galleries = [...this.querySelectorAll('media-gallery')];
    for (const gallery of galleries) /** @type {any} */ (gallery).reactive = true;

    return galleries;
  }

  /**
   * Point the hero at the active pair's product.
   *
   * @returns {boolean} Whether a gallery took it. False where there is none to give one to,
   *   and the caller has to send for the page instead.
   */
  #showMatchingProduct() {
    const products = this.#matchingProducts.filter(Boolean);
    if (!products.length) return false;

    const galleries = this.#galleries;
    if (!galleries.length) return false;

    // Every pair, in the order they were picked: the gallery opens on the first picture of
    // each side by side, so the set is seen as a set before either half's detail.
    for (const gallery of galleries) {
      /** @type {any} */ (gallery).products = products;
    }

    this.#showMatchingSetPrice();

    return true;
  }

  /**
   * Price the set rather than the pair the step was rendered for.
   *
   * The card belongs to one pair's product page, so every figure on it — price, renewal, what
   * the promo measures against, whether the promo applies at all — describes that pair twice.
   */
  #showMatchingSetPrice() {
    const card = this.querySelector('[data-purchase-option="subscription"]');
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('purchase-option-selector input[type="radio"]:checked')
    );
    const planName = checked?.dataset.sellingPlan ?? '';
    if (!card || !planName) return;

    let plan = 0;
    let full = 0;
    let eligible = true;

    for (const [index, selection] of this.selections.entries()) {
      const product = this.#matchingProducts[index];
      const variant = product?.variants?.find((/** @type {any} */ candidate) =>
        candidate.options?.includes(selection.size),
      );
      const line = this.#createSubscriptionLine(product, selection.size, planName);
      if (!line || !variant) return;

      plan += line.price;
      full += variant.compare_at_price || variant.price;
      if (!promoCoversProduct(product)) eligible = false;
    }

    const money = (/** @type {number} */ amount) =>
      formatMoney(amount, this.dataset.moneyFormat || '${{amount}}', window.Shopify?.currency?.active ?? 'USD');

    const price = card.querySelector('[data-purchase-option-price]');
    const promoPrice = price?.querySelector('utm-price-component');

    const compare = card.querySelector('[data-purchase-option-compare-at]');
    if (compare) compare.textContent = money(full);

    const renewal = card.querySelector('[data-purchase-option-renewal]');
    if (renewal) renewal.textContent = money(plan);

    // Every figure first, then the render, so no component prices itself from a half-corrected
    // card. Eligibility included: a matching set renders a hero once and swaps the gallery for
    // every print after, so the card can still be answering for the flow's own product.
    const promos = /** @type {any[]} */ ([...card.querySelectorAll('utm-price-component')]);

    for (const component of promos) {
      if (component.dataset.priceDifference) component.dataset.priceDifference = String(full);
      component.dataset.eligible = String(eligible);
    }

    // The price is told what to fall back to; the savings copy is the server's own and only
    // needs measuring again.
    for (const component of promos) {
      if (component === promoPrice) component.showPrice?.(money(plan));
      else component.refresh?.();
    }

    if (!promoPrice && price instanceof HTMLElement) price.textContent = money(plan);
  }

  /**
   * Build the print step for another pair. The pack builder is left alone: it holds what
   * the shopper has already added, which is not a per-pair thing.
   * @param {MouseEvent} event
   */
  switchTo(event) {
    // The button carries the index, not the chip around it: only one of the two buttons in
    // a chip switches, and the other goes to a step.
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-index]');
    const index = parseInt(/** @type {HTMLElement} */ (button)?.dataset.index ?? '-1', 10);
    if (index < 0 || index === this.activeSelection) return;

    this.activeSelection = index;

    // Changes with the chip, not only on arrival at this step.
    this.#showMatchingProduct();
    this.#updatePrintSubtitle();
    this.#renderSwitcher();
    this.#renderTopPrint();
    this.#syncReviewsProduct();
    this.#updateFeatures();
  }

  /**
   * Go back to a pair's own step to edit it, and come back here when it is done — rather
   * than replaying the steps in between.
   * @param {MouseEvent} event
   */
  editSelection(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-index]');
    const index = parseInt(/** @type {HTMLElement} */ (button)?.dataset.index ?? '-1', 10);
    const target = parseInt(/** @type {HTMLElement} */ (button)?.dataset.step ?? '0', 10);
    if (index < 0 || !target) return;

    this.#returnStep = this.currentStep;
    this.activeSelection = index;
    this.goToStep(target);
  }

  /**
   * Find the step an element sits in, or 0 when it sits outside the steps.
   * @param {Element} element
   * @returns {number}
   */
  #findStep(element) {
    const panel = element.closest('.product-builder__step-panel');

    return panel instanceof HTMLElement ? parseInt(panel.dataset.step ?? '0', 10) : 0;
  }

  /** @type {boolean} Whether the url has been read into the selections yet. */
  #urlRestored = false;

  /**
   * @type {URLSearchParams | null} The url as it was when this element connected. Read
   * then because applying waits for the selections to upgrade, and the `updateUI` before
   * that rewrites the address bar from a builder holding nothing.
   */
  #initialParams = null;

  /**
   * @type {boolean} True while the url is being applied. Each selection it restores
   * announces a change, and every one after the first would overwrite the url's own print
   * with Searchspring's pick.
   */
  #restoring = false;

  /**
   * Restore the url once everything it addresses can receive it.
   *
   * This element wraps the selections, and they wrap their grids, so both upgrade after it
   * does. Restoring immediately would call `selectByHandle` on an element that has no such
   * method yet, which throws and quietly leaves the whole url unapplied.
   *
   * Waiting on the definitions is what makes that deterministic: elements already in the
   * document upgrade when their definition lands, so once all three have been defined every
   * instance below is real.
   */
  async #restoreUrlWhenReady() {
    if (this.#urlRestored) return;
    this.#urlRestored = true;
    this.#initialParams = new URLSearchParams(location.search);

    // Only what this flow actually renders: a definition for an element no step asks for
    // never lands, and awaiting it would hold the whole url unapplied.
    const tags = ['product-builder-selection', 'product-builder-styles', 'product-builder-sizes'].filter((tag) =>
      this.querySelector(tag),
    );

    await Promise.all(tags.map((tag) => customElements.whenDefined(tag)));

    this.#applyUrlSelection();
    this.#revealRestoredTabs();
    this.updateUI();
  }

  /**
   * Open the tab holding each selection's style.
   *
   * A tab group opens its first tab by itself; a url can name a style in a later one, and
   * leaving that tab closed hides the very thing it asked for.
   */
  #revealRestoredTabs() {
    for (const selection of this.selections) {
      const style = selection.style;
      if (!style) continue;

      for (const group of selection.querySelectorAll('tab-group')) {
        if (/** @type {any} */ (group).reveal?.(style)) break;
      }
    }
  }

  /** A selection changed: redraw what depends on it. */
  #handleSelectionChange = (event) => {
    // The style or size moved, so whatever print was showing is no longer the right one.
    // Not while restoring: applying the url's style and size announces changes of its own,
    // and they would clear the print the same url just named.
    const changed = this.selections.indexOf(/** @type {CustomEvent} */ (event).detail?.selection);
    if (changed >= 0 && !this.#restoring) this.#prints[changed] = null;

    this.updateUI();
    this.#renderSwitcher();
    this.#refreshConfigRequesters();
    this.#syncReviewsProduct();
    this.#updateFeatures();

    // The url's own print is rendered once, after every selection it named has been applied.
    if (!this.#restoring) this.#renderTopPrint();
  };

  /** After the morph that brought the new variant in, not during it. */
  #reanswerAfterVariant = () => {
    queueMicrotask(() => this.#refreshConfigRequesters());
  };

  /**
   * Find the selection an element belongs to. It is the element it sits inside, so this is
   * a lookup rather than the reconstruction from step numbers it replaces.
   *
   * @param {Element | null | undefined} element
   * @returns {any} The selection, or null outside every one of them.
   */
  #findSelection(element) {
    return element?.closest('product-builder-selection') ?? null;
  }

  /**
   * Find a selection's own steps.
   *
   * @param {any} selection
   * @returns {number[]} Its step numbers, in order.
   */
  #findSteps(selection) {
    return (this.refs.stepPanel ?? [])
      .filter((panel) => selection?.contains(panel))
      .map((panel) => parseInt(panel.dataset.step ?? '0', 10))
      .filter(Boolean);
  }

  /**
   * @param {number} step
   * @returns {HTMLElement | undefined}
   */
  #panel(step) {
    return (this.refs.stepPanel ?? []).find((panel) => parseInt(panel.dataset.step ?? '0', 10) === step);
  }

  /**
   * Whether a step has the selection it needs to be left behind. A pair step needs its
   * own style, and its own size when it offers sizes; the print step has nothing to
   * satisfy.
   * @param {number} step
   * @returns {boolean}
   */
  #isSatisfied(step) {
    const panel = this.#panel(step);
    if (!panel) return true;

    // A category pick is held on the tile, not in a selection.
    if (panel.querySelector('.product-builder__categories') && !this.activeCategoryTile) return false;

    // A step asks for whatever it puts on screen — style, size, or both — of the selection
    // it belongs to. The print step asks for nothing.
    const selection = this.#findSelection(panel);
    if (!selection) return true;

    if (panel.querySelector('product-builder-styles') && !selection.style) return false;
    if (panel.querySelector('product-builder-sizes') && !selection.size) return false;

    return true;
  }

  /**
   * What a step is still waiting for, which is what its continue button says.
   *
   * Style before size, in the order the step asks for them: naming the size while no style
   * is picked would point at a grid the shopper has not reached.
   *
   * @param {number} step
   * @returns {'style' | 'size' | 'blocked' | 'ready'}
   */
  #ctaState(step) {
    const panel = this.#panel(step);
    const selection = panel ? this.#findSelection(panel) : null;

    if (!panel || !selection) return this.#isSatisfied(step) ? 'ready' : 'blocked';

    if (panel.querySelector('.product-builder__categories') && !this.activeCategoryTile) return 'blocked';

    if (panel.querySelector('product-builder-styles') && !selection.style) return 'style';
    if (panel.querySelector('product-builder-sizes') && !selection.size) return 'size';

    return 'ready';
  }

  /**
   * @param {number} step
   * @returns {boolean} Whether a step is the print step — the one with no picks of its own.
   */
  #isPrintStep(step) {
    const panel = this.#panel(step);

    return Boolean(panel) && !panel?.querySelector('product-builder-styles, product-builder-sizes');
  }

  /** @returns {boolean} Whether the current step can be left. */
  get canAdvance() {
    return this.#isSatisfied(this.currentStep);
  }

  /**
   * Whether a step is reachable: already visited, or every step between here and
   * there is satisfied.
   * @param {number} target
   * @returns {boolean}
   */
  #canReach(target) {
    if (target <= this.currentStep) return true;
    for (let step = this.currentStep; step < target; step++) {
      if (!this.#isSatisfied(step)) return false;
    }
    return true;
  }

  /**
   * The handleized label a step is addressed by in the URL.
   * @param {number} step
   * @returns {string}
   */
  #stepHandle(step) {
    const match = (this.refs.step ?? []).find((el) => parseInt(el.dataset.step ?? '0', 10) === step);
    return match?.dataset.stepHandle ?? '';
  }

  /**
   * The step number a URL handle refers to, or NaN if it matches no step.
   * @param {string | null} handle
   * @returns {number}
   */
  #stepFromHandle(handle) {
    if (!handle) return NaN;
    const match = (this.refs.step ?? []).find((el) => el.dataset.stepHandle === handle);
    return match ? parseInt(match.dataset.step ?? '0', 10) : NaN;
  }

  /**
   * Called by the on:click binding on each progress-indicator step.
   * @param {MouseEvent} event
   */
  selectStep(event) {
    const target = parseInt(/** @type {HTMLElement} */ (event.target).dataset.step ?? '0', 10);
    if (target && this.#canReach(target)) this.goToStep(target);
  }

  nextStep() {
    if (!this.canAdvance) return;

    // An edit reached from a chip goes back where it came from, rather than replaying
    // the steps in between.
    const step = this.#returnStep ?? this.currentStep + 1;
    this.#returnStep = null;

    this.goToStep(step);
  }

  prevStep() {
    this.goToStep(this.currentStep - 1);
  }

  /**
   * Switch to a given step (1-based), running any per-step setup.
   * @param {number} step
   */
  goToStep(step) {
    const target = Math.min(Math.max(step, 1), this.maxStep);
    if (target === this.currentStep) return;

    this.currentStep = target;

    // After the step changes, not before: they read the selection in play, and on the print
    // step that is whichever chip is active — which the step being left would answer wrong.
    // The chips summarise what each pair picked, so they are rebuilt on arrival rather than
    // kept in step with every click inside a pair.
    if (target === this.maxStep) {
      this.#updatePrintSubtitle();
      this.#renderSwitcher();
      // The pair whose step was just left put its own print in the hero, and that is not the
      // pair the active chip names.
      this.#renderTopPrint();
    }

    this.updateUI();
    this.#scrollToStep();
  }

  /**
   * Bring the title of the step the shopper moved to up to the top of the viewport. One
   * already on screen is left alone, so changing step does not yank the page.
   */
  #scrollToStep() {
    const anchor = this.#stepAnchor;
    if (!anchor) return;

    // Measured against the sticky header, which covers the top of the viewport.
    const header = parseFloat(getComputedStyle(anchor).getPropertyValue('--header-height')) || 0;
    const { top, bottom } = anchor.getBoundingClientRect();
    if (top >= header && bottom <= window.innerHeight) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    anchor.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }

  /**
   * Where the current step begins: its title, or its step bar button when it renders none.
   * Both carry the scroll margin that clears the sticky header.
   */
  get #stepAnchor() {
    const panel = (this.refs.stepPanel ?? [])[this.currentStep - 1];
    // Titles are optional, so the refs do not line up with the steps — the active panel owns
    // at most one of them.
    const title = (this.refs.stepTitle ?? []).find((el) => !el.hidden && panel?.contains(el));

    return title ?? (this.refs.step ?? [])[this.currentStep - 1];
  }

  /**
   * Name the selection under the print step's heading — "Flyless Boxer | Size: M".
   * Falls back to the section's own text when nothing is chosen yet, so the step
   * still reads sensibly if a shopper lands on it early.
   */
  #updatePrintSubtitle() {
    // Every step renders a subheading, so only the one belonging to the step that
    // holds the product hero is rewritten — the rest keep their authored text.
    const subtitles = (this.refs.printSubtitle ?? []).filter((subtitle) =>
      subtitle.closest('.product-builder__step-panel')?.querySelector('.product-builder__print'),
    );
    if (subtitles.length === 0) return;

    const label = this.selectedIcon?.dataset.label;
    const size = this.selectedSize;
    if (!label || !size) return;

    const text = (this.dataset.printSubtitleText ?? '').split('[style]').join(label).split('[size]').join(size);
    for (const subtitle of subtitles) subtitle.textContent = text;
  }

  /** Show the step in play and no other. */
  #openStepPanel() {
    (this.refs.stepPanel ?? []).forEach((panel, index) => {
      panel.hidden = index + 1 !== this.currentStep;
    });
  }

  /**
   * Reflect the current step across panels, progress indicator, tabs, and CTAs.
   */
  updateUI() {
    this.dataset.currentStep = String(this.currentStep);

    for (const panel of this.refs.stepPanel ?? []) {
      const step = parseInt(panel.dataset.step ?? '0', 10);
      panel.hidden = step !== this.currentStep;
    }

    for (const circle of this.querySelectorAll('.product-builder__step-circle')) {
      const step = parseInt(/** @type {HTMLElement} */ (circle).dataset.step ?? '0', 10);
      circle.classList.toggle('is-done', step < this.currentStep);
      circle.classList.toggle('is-active', step === this.currentStep);
    }

    for (const step of this.refs.step ?? []) {
      const index = parseInt(step.dataset.step ?? '0', 10);
      if (index === this.currentStep) {
        step.setAttribute('aria-current', 'step');
      } else {
        step.removeAttribute('aria-current');
      }
      // aria-disabled rather than `disabled` so an out-of-reach step stays
      // focusable and announced; selectStep is what actually blocks the jump.
      step.setAttribute('aria-disabled', this.#canReach(index) ? 'false' : 'true');
    }

    // Controls that advance are blocked together, so the arrow can't silently no-op.
    const canLeave = this.canAdvance;
    this.#setBlocked(this.refs.prevArrow, this.currentStep <= 1);
    this.#setBlocked(this.refs.nextArrow, this.currentStep >= this.maxStep || !canLeave);

    // Every step's continue button is blocked until that step is satisfied, and says which
    // of its own picks is still missing.
    (this.refs.stepContinue ?? []).forEach((cta, i) => {
      const state = this.#ctaState(i + 1);

      cta.dataset.ctaState = state;
      this.#setBlocked(cta, state !== 'ready');
    });

    // The print step's markup arrives from a fetch, so the row this publishes to may not be
    // here yet; #renderPrint publishes again once it is.
    this.#publishSelections();
    this.#syncUrl();
  }

  /**
   * `aria-disabled`, not `disabled`, which would drop the control from the tab
   * order and hide it from assistive tech. Handlers do the actual refusing.
   * @param {HTMLElement} [control]
   * @param {boolean} blocked
   */
  #setBlocked(control, blocked) {
    control?.setAttribute('aria-disabled', blocked ? 'true' : 'false');
  }

  /**
   * Select the style an emitter named, so a link into the builder opens on it.
   *
   * @param {unknown} detail - A style handle, or an object carrying one as `style`.
   * @returns {boolean} Whether a style the emitter named was found and applied.
   */
  #preselectStyle(detail) {
    const style = typeof detail === 'string' ? detail : /** @type {{ style?: string }} */ (detail)?.style;
    if (!style) return false;

    // Whichever selection offers that style takes it; the rest are another pair's.
    for (const selection of this.selections) {
      if (!selection.styleGrid?.selectByHandle(style)) continue;

      this.updateUI();

      return true;
    }

    return false;
  }

  /**
   * The first step that renders `selector`, or 0 when none does. Steps are theme blocks, so
   * only the markup says which number asks for what.
   *
   * @param {string} selector
   * @returns {number}
   */
  #stepAsking(selector) {
    const panel = (this.refs.stepPanel ?? []).find((step) => step.querySelector(selector));

    return panel ? parseInt(panel.dataset.step ?? '0', 10) : 0;
  }

  /** Re-announce the current style, for a features section that connected after this one. */
  #announceFeatures = () => this.#updateFeatures(true);

  /**
   * Tell a config accordion what the flow has picked.
   *
   * It renders with the print template and knows the product it is dressing, never the
   * category that led there, so both rows are named from here.
   *
   * @param {Event} event
   */
  #answerConfigRequest = (event) => {
    const requester = /** @type {any} */ (event.target);
    if (typeof requester?.render !== 'function') return;

    // Whoever asked is answered again when the answer changes, for as long as it is on
    // the page: the print step is refetched often, and each one brings its own.
    this.#configRequesters.add(requester);

    const category = this.activeCategoryTile;
    const style = this.selectedIcon;

    // Left out rather than sent empty: a path resolving to nothing leaves the markup
    // Liquid rendered, which is the loading state.
    requester.render({
      product: {
        value: category?.getAttribute('aria-controls') || null,
        label: category?.dataset.label || null,
        image: category?.dataset.image || null,
        options: this.#categoryOptions(),
      },
      // Empty rather than absent: a category change leaves no style, and an absent field
      // would leave the last one's name sitting in the row.
      style: {
        value: style?.dataset.style ?? '',
        label: style?.dataset.label ?? '',
        image: style?.dataset.iconSrc ?? '',
        options: this.#styleOptions(),
      },
    });
  };

  /**
   * Take a category chosen from the cross-sell: the same move as picking it in the accordion,
   * so the flow goes back to asking for a style and the pack it has built stays as it is.
   *
   * @param {Event} event
   */
  #takeCrossSell = (event) => {
    const value = /** @type {CustomEvent} */ (event).detail?.value;
    const tile = (this.refs.categoryTile ?? []).find((candidate) => candidate.getAttribute('aria-controls') === value);
    if (!tile) return;

    this.#pickCategory(tile);
    this.#forgetSelection();
    this.updateUI();
    this.#refreshConfigRequesters();

    this.#askForStyle();
  };

  /**
   * Open the style row and bring it into view.
   *
   * A type on its own is half an answer, and changing one empties the step below it — on a
   * small screen the page collapses by most of its height, leaving the question off screen.
   */
  #askForStyle() {
    for (const accordion of this.querySelectorAll('product-builder-accordion')) {
      const row = /** @type {any} */ (accordion).openRow?.('style');
      // One of the two copies is hidden at any width — scrolling to that one moves the
      // page to a question nobody can see.
      if (row && accordion.checkVisibility()) this.#showAccordionRow(row);
    }
  }

  /**
   * Bring an open accordion row into view under the sticky header.
   *
   * Measured from the top of the accordion rather than the row's own: the rows above
   * name what has already been picked, and pushing them behind the header leaves the
   * question with nothing to answer it against.
   *
   * @param {HTMLElement} row
   */
  #showAccordionRow(row) {
    const panel = row.querySelector('.product-builder__accordion-panel');
    panel.scrollIntoView({ behavior: 'instant', block: 'center' });
  }

  /**
   * Offer the categories this bundle has not drawn from yet.
   *
   * The asker names what it already holds, because only it knows what was added; this knows
   * what else there is. An empty answer means the bundle already spans everything.
   *
   * @param {Event} event
   */
  #answerCrossSell = (event) => {
    const requester = /** @type {any} */ (event.target);
    if (typeof requester?.render !== 'function') return;

    const used = new Set(/** @type {CustomEvent} */ (event).detail?.used ?? []);
    const categories = this.#categoryOptions().filter((option) => !used.has(option.value));

    requester.render({ categories });
  };

  /**
   * Every category on offer, for whoever is asking to draw them.
   *
   * Addressed by the panel each tile controls: that is what the tile already names, and
   * what picking one comes back as.
   *
   * `current` is the word rather than a boolean: a bound boolean adds or removes an
   * attribute, and aria-pressed has to say which of the two it is.
   *
   * @returns {{ value: string, label: string, image: string | null, count: string, current: string }[]}
   */
  #categoryOptions() {
    return (this.refs.categoryTile ?? []).map((tile) => ({
      value: tile.getAttribute('aria-controls') ?? '',
      label: tile.dataset.label ?? '',
      image: tile.dataset.image || null,
      count: tile.dataset.count ?? '',
      current: tile.getAttribute('aria-pressed') === 'true' ? 'true' : 'false',
    }));
  }

  /**
   * The grid the category in play offers its styles from.
   *
   * @returns {any} The grid, or null before a category has been picked.
   */
  #activeStyleGrid() {
    const panelId = this.activeCategoryTile?.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;

    return panel?.querySelector('product-builder-styles') ?? null;
  }

  /**
   * The styles the category in play offers, for whoever is asking to draw them.
   *
   * No count beneath these: a style is one thing, where a category is a number of them.
   *
   * @returns {{ value: string, label: string, image: string | null, count: string, current: string }[]}
   */
  #styleOptions() {
    return (this.#activeStyleGrid()?.tiles ?? []).map((tile) => ({
      value: tile.dataset.style ?? '',
      label: tile.dataset.label ?? '',
      image: tile.dataset.iconSrc || null,
      count: '',
      current: tile.classList.contains('is-selected') ? 'true' : 'false',
    }));
  }

  /**
   * A confirmed row: apply it.
   *
   * @param {Event} event
   */
  #handleConfigConfirm = (event) => {
    const { key, value } = /** @type {CustomEvent} */ (event).detail ?? {};
    if (!value) return;

    if (key === 'product') {
      const tile = (this.refs.categoryTile ?? []).find(
        (candidate) => candidate.getAttribute('aria-controls') === value,
      );
      if (!tile) return;

      this.#pickCategory(tile);
      this.#forgetSelection();
      this.updateUI();
      this.#refreshConfigRequesters();
      this.#askForStyle();
      return;
    }

    if (key !== 'style') return;

    const grid = this.#activeStyleGrid();
    const tile = (grid?.tiles ?? []).find((candidate) => candidate.dataset.style === value);

    // Chosen, not restored: what follows a pick — the sizes, the prints, the hero — is
    // only welcome when a shopper asked for it, and here one did.
    if (tile) grid.select(tile, true);
  };

  /**
   * Drop what the last category answered.
   *
   * A new category asks the style question again, so nothing is preselected and the
   * size goes with it. The print step keeps its markup — the rows doing the asking are
   * part of it — and hides what no longer describes anything.
   */
  #forgetSelection() {
    for (const selection of this.selections) {
      for (const grid of selection.styleGrids ?? []) grid.clear?.();
      selection.sizeGrid?.forgetSize?.();
    }

    // Sizes do not carry across product types — a sock size means nothing on a tee — so the
    // print step's own answer goes too, along with the variant naming it in the url.
    for (const input of this.#printSizeInputs()) {
      input.checked = false;
      // The slide's own bookkeeping, which morph carries across by position: left set, the
      // pill is drawn over whichever size lands at that index on the next product.
      input.dataset.currentChecked = 'false';
      input.dataset.previousChecked = 'false';
    }

    const url = new URL(location.href);
    if (url.searchParams.has('variant')) {
      url.searchParams.delete('variant');
      history.replaceState(null, '', url.href.replace(/%2C/g, ','));
    }

    // Unchecking an input fires nothing, so anything holding its own note of the size is
    // told. The pack builder gates its add slots on one.
    document.dispatchEvent(new CustomEvent('product-builder:size-forgotten', { bubbles: true }));

    this.#prints = this.#prints.map(() => null);

    // Cleared so the next style re-renders: the guard is a "already showing this" test,
    // and what is showing no longer belongs to the category in play.
    this.#renderedPrint = undefined;

    const host = this.refs.printTarget;
    if (host) host.dataset.reconfiguring = 'true';
  }

  /** @type {Set<any>} Whoever has asked what the flow has picked. */
  #configRequesters = new Set();

  /** Re-answer them, after a pick changes what they were told. */
  #refreshConfigRequesters() {
    for (const requester of this.#configRequesters) {
      if (requester.isConnected) this.#answerConfigRequest({ target: requester });
      else this.#configRequesters.delete(requester);
    }
  }

  /** @type {string | null} The product features were last pointed at. */
  #shownFeatures = null;

  /**
   * Point the features section at the selected style's product.
   *
   * `target` is a merchant-set features id rather than a section id, so this stays
   * ignorant of where the section is. Unset addresses every one on the page.
   *
   * @param {boolean} [force] - Emit even where the product has not changed, for a section
   *   that connected after this one and is asking what to show.
   * @param {string | null} [namedProductUrl] - The product to show, for a caller that knows
   *   one before a style has been picked.
   */
  #updateFeatures(force = false, namedProductUrl = null) {
    const productUrl = namedProductUrl ?? this.selectedIcon?.dataset.productUrl;
    if (!productUrl) return;

    // The style names the product, so a size or print pick leaves it alone.
    if (!force && productUrl === this.#shownFeatures) return;
    this.#shownFeatures = productUrl;

    const detail = { productUrl, target: this.dataset.featuresId ?? '' };

    this.dispatchEvent(new CustomEvent('features:update', { bubbles: true, detail }));
  }

  /**
   * Point Okendo's widgets at the selected style's reviews product.
   *
   * The widgets are page sections outside this component, and they render once for
   * whichever product the template named, so switching style has to tell them.
   * Okendo loads asynchronously, so a selection made before its script arrives is
   * held and applied when it does.
   *
   * @param {string} [productId] - The reviews product, for a caller that knows one before
   *   a style has been picked.
   */
  async #syncReviewsProduct(productId) {
    const id = productId ?? this.selectedIcon?.dataset.reviewsProductId;
    if (!id) return;

    this.#pendingReviewsProduct = id;
    const api = await this.#okendoApi();
    // A later pick won while this was waiting.
    if (!api || this.#pendingReviewsProduct !== id) return;

    for (const widget of document.querySelectorAll('[data-oke-reviews-product-id]')) {
      api.setProduct(widget, `shopify-${id}`);
    }
  }

  /** @type {string | null} */
  #pendingReviewsProduct = null;

  /**
   * Okendo's script is injected by the app and can land after this component
   * upgrades, so the api is waited for rather than assumed.
   * @returns {Promise<{ setProduct: (el: Element, id: string) => void } | null>}
   */
  async #okendoApi() {
    const deadline = 5000;
    const step = 100;
    for (let waited = 0; waited <= deadline; waited += step) {
      const api = /** @type {any} */ (window).okeWidgetApi;
      if (api?.setProduct) return api;
      await new Promise((resolve) => setTimeout(resolve, step));
    }
    return null;
  }

  /**
   * Render the print Searchspring ranks first for the chosen style and size, falling
   * back to the style's own product when the query gives nothing.
   *
   * The builder asks rather than the selector: the selector arrives inside the hero
   * this fetch produces, so there is none on the page yet.
   */
  async #renderTopPrint() {
    // A print already chosen for this selection — picked, or named by the url — wins over
    // Searchspring's ranking. Changing the style clears it, so this never keeps a print the
    // new style has no version of.
    const chosen = this.selectedPrint;
    if (chosen) {
      this.#renderPrint(`${window.Shopify?.routes?.root ?? '/'}products/${chosen}`);
      return;
    }

    const top = await this.#topPrint();
    const url = top?.url ?? this.selectedIcon?.dataset.productUrl;
    if (url) this.#renderPrint(url, top);
  }

  /**
   * The Shopify tag behind the section's chosen print group, read off the style's own
   * Print Groups entries. Entries are `<label>`, `<label> | tag: <tag>` or
   * `<label> | handle: <token>`; only a tag can be asked of Searchspring, so the other
   * forms fall through to the overall ranking.
   * @returns {string | null}
   */
  #defaultPrintGroupTag() {
    const wanted = this.dataset.defaultPrintGroup?.trim().toLowerCase();
    if (!wanted) return null;

    let groups;
    try {
      groups = JSON.parse(this.selectedIcon?.dataset.printGroups ?? '[]');
    } catch {
      return null;
    }

    for (const entry of groups) {
      const [label, tag] = String(entry).split(' | tag: ');
      if (label?.trim().toLowerCase() === wanted && tag) return tag.trim();
    }
    return null;
  }

  /**
   * Searchspring's first result for this style and size, or null. The whole result,
   * not just its url: its variants are what resolve the size to a variant id.
   * @returns {Promise<any | null>}
   */
  async #topPrint() {
    const siteId = this.dataset.searchspringSiteId;
    const productType = this.selectedIcon?.dataset.productTypeConfig;
    if (!siteId || !productType) return null;

    const params = new URLSearchParams({ resultsFormat: 'native', siteId, resultsPerPage: '1' });
    // Same in-stock guard the print selector applies, so the two agree on what exists.
    params.set('bgfilter.mfield_denver_inventory_total_product_quantity.low', '1');
    params.set('filter.product_type', productType);
    if (this.selectedSize) params.set('filter.ss_size', this.selectedSize);

    // The group the shopper has open first, then the section's default — otherwise the
    // style's first print comes from another tab and is replaced a moment later.
    const groupTag = this.#printGroupTag ?? this.#defaultPrintGroupTag();
    if (groupTag) params.set('filter.tags', groupTag);

    try {
      const response = await fetch(buildSearchUrl(siteId, params));
      if (!response.ok) return null;
      const data = await response.json();
      const result = data?.results?.[0];
      return typeof result?.url === 'string' && result.url ? result : null;
    } catch {
      return null;
    }
  }

  /** @param {Event} event */
  #handlePrintPick = (event) => {
    event.preventDefault();
    const result = /** @type {CustomEvent} */ (event).detail;
    // A matching set is answered by a print carrying a garment for every pair. One carrying a
    // single product is the selector's own first answer, from before it was told which pairs
    // it is dressing, and it names a print only one of them can wear.
    if (this.#isMatchingSet && !(Array.isArray(result?.products) && result.products.length > 1)) return;

    // Several garments in one print, so there is no single page to show. The pair on screen
    // is the one the hero takes; a gallery this drives takes it directly, and any other still
    // has to be sent for.
    if (Array.isArray(result?.products) && result.products.length > 1) {
      this.#matchingProducts = result.products;
      this.#wearMatchingPrint(result);

      if (this.#showMatchingProduct()) return;

      const match = result.matches?.[this.activeSelection] ?? result.matches?.[0];
      const path = match?.url ?? this.#matchingProducts[this.activeSelection]?.url;
      if (path) this.#renderPrint(path, match ?? result);

      return;
    }

    const url = result?.url;
    if (typeof url !== 'string' || !url) return;

    // A pick is the last word. The guard against re-rendering what is already shown answers
    // a repeat of the same request, and a size change puts one in flight for the print the
    // size just ruled out — left standing, that one finishes last and wins.
    this.#renderedPrint = undefined;
    this.#renderPrint(url, result);
  };

  /**
   * Record the garment every pair wears in the print just picked.
   *
   * The whole set changes print together, so a pair that is not on screen is dressed too —
   * left alone it keeps whichever print it was ranked on its own before the set was matched,
   * which is a different print, and switching to that chip shows it.
   *
   * @param {any} result - The print's Searchspring result, one match per selection.
   */
  #wearMatchingPrint(result) {
    const matches = Array.isArray(result?.matches) ? result.matches : [];

    this.#matchingProducts.forEach((product, index) => {
      const handle = productHandleFromUrl(matches[index]?.url ?? product?.url ?? '');
      if (handle) this.#prints[index] = handle;
    });

    this.#syncUrl();
  }

  /**
   * Buy a matching set as two subscriptions rather than one.
   *
   * Each pair is its own line on its own plan — the plans differ by garment, and their prices
   * with them — held together by a shared `Matching UUID`, which is what the subscription
   * pipeline reads to treat the two as one. A quarterly plan groups its lines under its own id
   * instead, which the purchase option stamps for every flow. There is no parent line: the
   * pairs carry the whole description of what was bought.
   *
   * A one-time set has no plan to split on and stays the single line the form built.
   *
   * @param {Event} event
   */
  #handleCartLines = (event) => {
    if (!this.#isMatchingSet) return;

    const lines = /** @type {CustomEvent} */ (event).detail?.lines;
    if (!Array.isArray(lines) || !lines.length) return;

    // True whether or not the add splits below, so it is stamped before the split.
    for (const line of lines) line.properties = { ...line.properties, ...MATCHING_SET_PROPERTIES };

    // Whatever the purchase option stamped on the form's own line belongs on both of these,
    // the shared `__subscriptionId` among it.
    const set = this.#createSubscriptionLines(lines[0]?.properties ?? {});
    if (set.length !== this.selections.length) return;

    lines.length = 0;
    lines.push(...set);
  };

  /**
   * One cart line per pair, each on the plan its own product offers under the name the shopper
   * chose. Empty where any pair cannot be resolved, which leaves the form's own line alone.
   *
   * @param {Record<string, string>} base - Properties every line in this add carries.
   * @returns {any[]}
   */
  #createSubscriptionLines(base) {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('purchase-option-selector input[type="radio"]:checked')
    );
    const planName = checked?.dataset.sellingPlan ?? '';
    if (!planName) return [];

    // What ties the two pairs into one subscription. Scoped from the option itself: this
    // element is an ancestor of the step the option lives in, and the lookup only walks up.
    const set = getSubscriptionBoxProperties(checked ?? undefined, { matching: true });

    const lines = [];

    this.selections.forEach((/** @type {any} */ selection, index) => {
      const line = this.#createSubscriptionLine(this.#matchingProducts[index], selection.size, planName);
      if (!line) return;

      lines.push({
        ...line,
        // The cart's subscription properties describe a line whose product is the plan rather
        // than the garment. This line is the garment, on a real selling plan, so its own
        // title, variant and plan already say everything they would have said.
        properties: { ...base, ...set },
      });
    });

    return lines;
  }

  /**
   * One pair's line: the variant in the size it was picked in, on the plan of that name.
   *
   * The plan is looked up by name in the product's own groups and then by id among the
   * variant's allocations — an allocation names only the id, and the same plan carries a
   * different id on every product it is offered on.
   *
   * @param {any} product - The pair's product, as the Ajax Product API describes it.
   * @param {string} size
   * @param {string} planName
   * @returns {{ variantId: number, quantity: number, sellingPlanId: number, price: number } | null}
   */
  #createSubscriptionLine(product, size, planName) {
    const variant = product?.variants?.find((/** @type {any} */ candidate) => candidate.options?.includes(size));
    if (!variant) return null;

    const plans = (product.selling_plan_groups ?? []).flatMap((/** @type {any} */ group) => group.selling_plans ?? []);
    const plan = plans.find((/** @type {any} */ candidate) => candidate.name === planName);
    if (!plan) return null;

    const allocation = (variant.selling_plan_allocations ?? []).find(
      (/** @type {any} */ candidate) => candidate.selling_plan_id === plan.id,
    );
    if (!allocation) return null;

    return {
      variantId: variant.id,
      quantity: 1,
      sellingPlanId: plan.id,
      price: allocation.price,
    };
  }

  /** @type {any[]} One product per selection, in the order they were picked. */
  #matchingProducts = [];

  /** @type {string | undefined} The print currently in the step, so a repeat pick is free. */
  #renderedPrint;

  /**
   * The variant id for the chosen size, off a Searchspring result's own sizes.
   *
   * Costs no request, which is the point: it is the only thing standing between a print
   * click and the section fetch. Returns null when the result carries no matching size,
   * and `#variantIdForSize` then reads it from the product.
   *
   * @param {any} result
   * @returns {string | null}
   */
  #variantIdFromResult(result) {
    const variant = findVariantBySize(result, this.selectedSize);

    return variant?.variant_id ? String(variant.variant_id) : null;
  }

  /**
   * The variant id on `path` matching the chosen size.
   *
   * Read from the product, so a result whose variants do not match this storefront's
   * still resolves. The fetch is why `#variantIdFromResult` is tried first.
   * @param {string} path
   * @returns {Promise<string | null>}
   */
  async #variantIdForSize(path) {
    const size = this.selectedSize?.trim().toLowerCase();
    if (!size) return null;

    try {
      let product = this.#productCache.get(path);
      if (!product) {
        const response = await fetch(`${path}.js`);
        if (!response.ok) return null;
        product = await response.json();
        this.#productCache.set(path, product);
      }

      const match = (product.variants ?? []).find((variant) =>
        [variant.option1, variant.option2, variant.option3].some((option) => option?.trim?.().toLowerCase() === size),
      );
      return match?.id ? String(match.id) : null;
    } catch {
      return null;
    }
  }

  /**
   * Swap the print step's product for the picked print.
   *
   * Fetched from the product template named by the section's print step setting.
   * The first fetch renders that template to learn its section id; every later one
   * asks for that section alone.
   * @param {string} productUrl
   * @param {object | null} [result] - The Searchspring result the url came from.
   * @param {boolean} [allowStoredSection] - False on the retry after a stale section id.
   */
  async #renderPrint(productUrl, result = null, allowStoredSection = true) {
    const host = this.refs.printTarget;
    if (!host) return;

    // Read now, not when the fetch lands: a chip switched in between would otherwise take
    // this print as its own, overwriting what it already had.
    const index = this.#selectionIndex;

    // The tab is the shopper's, not the product's: a new style re-renders the selector and
    // would otherwise open on whichever group the server put first.
    const swatches = /** @type {any} */ (this.querySelector('ss-print-swatches'));
    if (swatches?.openGroup) {
      this.#printGroup = swatches.openGroup;
      this.#printGroupTag = swatches.openGroupTag ?? null;
    }

    // A previous page load already learned it, so the first print here is cheap too.
    if (allowStoredSection && !this.#printSectionId) {
      try {
        this.#printSectionId = sessionStorage.getItem(this.#printSectionKey);
      } catch {
        // storage unavailable; the template render below learns it again
      }
    }

    // Searchspring returns absolute production urls, so only the path is kept: this has
    // to resolve against whatever host the theme is being served from.
    const source = new URL(productUrl, window.location.origin);
    const url = new URL(source.pathname, window.location.origin);
    const view = this.dataset.printTemplate;
    if (!view) return;

    // Once the section id is known, ask for that section alone. The first print has to
    // render the template to learn it, and that response is the print anyway.
    const bySection = Boolean(this.#printSectionId) && allowStoredSection;
    // `sectionRenderer` sets and sorts `section_id` itself, and its cache is keyed on the
    // result — so setting it here too would key the same request two ways.
    if (!bySection) url.searchParams.set('view', view);
    // `variant`, not `option_values`: only the variant id resolves the hero's cart form,
    // and without it the form has no variant input at all. The picked result usually
    // carries it, which saves a request before the section can even be asked for.
    const variantId = this.#variantIdFromResult(result) ?? (await this.#variantIdForSize(source.pathname));
    if (variantId) url.searchParams.set('variant', variantId);

    const key = url.toString();
    if (this.#renderedPrint === key) return;
    this.#renderedPrint = key;

    // The blank tile stays until the new print is in: cleared here, the rows it hides
    // would come back one at a time over the fetch. `loading` only marks the wait, so an
    // ordinary print change — which morphs in place — is left alone.
    host.dataset.loading = 'true';

    this.#printAbortController?.abort();
    this.#printAbortController = new AbortController();

    try {
      let html = this.#printCache.get(key);
      if (!html && bySection) {
        // Through `sectionRenderer`, so this shares the cache and the in-flight dedupe with
        // the swatch selector's hover prefetch, which asks for the same section at the same
        // url. A hovered print is therefore already fetched by the time it is clicked.
        html = await sectionRenderer.getSectionHTML(
          /** @type {string} */ (this.#printSectionId),
          true,
          new URL(`${url.pathname}${url.search}`, window.location.origin),
        );
      }
      if (!html) {
        const response = await fetch(key, { signal: this.#printAbortController.signal });
        if (!response.ok) throw new Error(`Print fetch failed: ${response.status}`);
        html = await response.text();
        this.#printCache.set(key, html);
      }

      // `sectionRenderer` has no abort, so a slower earlier print could land after a newer
      // pick. The key is the newest request, so anything else is stale.
      if (this.#renderedPrint !== key) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      // The component, not the section wrapper: same tag on both sides, and the
      // wrapper's JSON-LD stays out of the page.
      const wrapper = doc.querySelector('[id$="__product"]');
      const incoming = wrapper?.querySelector('featured-product-information');
      if (!wrapper || !incoming) throw new Error('Product hero missing from the print response');

      // Learned from the full render so the next print, and the next page load, skip it.
      if (!this.#printSectionId) {
        this.#printSectionId = wrapper.id.replace(/^shopify-section-/, '');
        try {
          sessionStorage.setItem(this.#printSectionKey, this.#printSectionId);
        } catch {
          // storage unavailable; it is re-learned next time
        }
      }

      // The incoming markup carries its own default cadence, so the shopper's pick is
      // read off first and re-checked after — swapping print must not reset it.
      const checkedOption = getCheckedPurchaseOption(host);

      // A gallery this component drives holds the print the shopper settled on, which the
      // markup arriving here does not know about — it is one pair's product page. Lifted out
      // before the morph and put back after, because morph replaces a node's children
      // wholesale and its reject hook is never asked about them.
      const driven = this.#galleries.find((gallery) => host.contains(gallery));
      driven?.remove();

      // First print adopts the fetched hero; later ones morph onto it, so the shopper's
      // scroll position and any open media survive a print change.
      const current = host.querySelector('featured-product-information');
      if (current) {
        morph(current, incoming, MORPH_OPTIONS);
      } else {
        host.replaceChildren(document.importNode(incoming, true));
      }

      if (driven) host.querySelector('media-gallery')?.replaceWith(driven);

      // The print row arrived with that markup, so it learns what it is answering for now
      // rather than at the last step change, which was before it existed.
      this.#publishSelections();

      // Adopted before anything is rendered into them, so a gallery is exempt from the next
      // render as well as this one.
      void this.#galleries;

      // A script that arrives in fetched markup never runs — the browser only executes
      // one it parses. Re-inserting a copy does, and a module already loaded is deduped
      // by url, so a second print costs nothing.
      for (const script of host.querySelectorAll('script')) {
        const replacement = document.createElement('script');
        for (const { name, value } of script.attributes) replacement.setAttribute(name, value);
        replacement.textContent = script.textContent;
        script.replaceWith(replacement);
      }

      // The morph left the selector's own node in place but replaced its tabs, so the
      // resize observer that positions the active pill is watching nodes that are gone.
      // Re-inserting re-runs connectedCallback, which observes the new tabs.
      const selector = host.querySelector('ss-print-swatches');
      if (selector?.parentElement) {
        const parent = selector.parentElement;
        const next = selector.nextSibling;
        selector.remove();
        parent.insertBefore(selector, next);
      }

      if (checkedOption) restorePurchaseOption(host, checkedOption);

      // The switcher arrives with the hero, so its ref only exists once this markup is in.
      // The observer would catch it eventually; the switcher has to be filled now.
      this.updatedCallback();
      this.#renderSwitcher();
      // The rows arrived with this render and asked before the pick that caused it had
      // settled, so they are answered again now it has.
      this.#refreshConfigRequesters();

      this.#prints[index] = productHandleFromUrl(url.pathname) || null;
      this.#syncUrl();

      // The selector came with the hero, so it is pointed at the wizard's own choices.
      this.#syncPrintFilters();

      for (const element of host.querySelectorAll('purchase-options, buy-buttons')) {
        if (element instanceof Component && element.isConnected) element.updatedCallback?.();
      }
      // A slideshow is wired on connect for the slides it had then, and a Mystery print carries a
      // single picture — so its gallery was set up with no scroller at all, and the arrows and
      // thumbnails of the print morphed over it do nothing. `morph` hands its post-update hook the
      // parsed response node, so `updatedCallback` never reaches the element actually morphed; the
      // gallery and its slideshow are told here instead.
      const gallery = host.querySelector('media-gallery');
      if (gallery instanceof MediaGallery) {
        gallery.updatedCallback();
        gallery.slideshow?.updatedCallback();
        gallery.slideshow?.select(0, undefined, { animate: false });
      }

      // Everything the print brought is in place, so the step is shown in one go.
      delete host.dataset.reconfiguring;
      delete host.dataset.loading;

      // The picker arrived with it, so what was chosen may no longer be on offer. Anything
      // gating on a size re-reads it rather than trusting the answer it last saw.
      this.#publishCategory();
      document.dispatchEvent(new CustomEvent('product-builder:print-rendered', { bubbles: true }));
    } catch (error) {
      // A newer render owns the flags now, and clears them itself.
      if (error instanceof DOMException && error.name === 'AbortError') return;

      // A stored id goes stale when the template is recreated, so the template render
      // is tried once more to learn the new one.
      if (bySection) {
        this.#printSectionId = null;
        try {
          sessionStorage.removeItem(this.#printSectionKey);
        } catch {
          // nothing to clear
        }
        this.#renderedPrint = undefined;
        await this.#renderPrint(productUrl, result, false);
        return;
      }

      // Cleared so picking this print again retries; the guard above is a
      // "already showing this" test, and a failed render is not showing it.
      this.#renderedPrint = undefined;
      // Leave the print step showing whatever it already had — but not spinning, since
      // nothing is coming.
      delete host.dataset.loading;
    }
  }

  /**
   * Point the print selector at the chosen style and size — the two Searchspring
   * filters — and fetch. Called when the size is picked, so the prints are in hand
   * before the print step is reached.
   *
   * The fetch is explicit because the selector loads itself lazily, on intersecting
   * the viewport, and it sits in a step panel that is still `hidden` at this point.
   */
  #syncPrintFilters() {
    // querySelector, not a ref: the selector is its own component, and refs stop there.
    const swatches = this.querySelector('ss-print-swatches');
    if (!(swatches instanceof HTMLElement)) return;

    this.#namePrintToSelector();

    const productType = this.selectedIcon?.dataset.productTypeConfig;
    if (productType) swatches.setAttribute('data-product-type', productType);
    if (this.selectedSize) swatches.setAttribute('data-size', this.selectedSize);

    // Before the fetch below, which asks about whichever group is open.
    if ('presetGroup' in swatches && typeof swatches.presetGroup === 'function') {
      swatches.presetGroup(this.#printGroup);
    }

    // `filtering`, because this narrows an answer the shopper already gave — the size or the
    // product type changed, not the question. Asked afresh, the selector settles on the first
    // swatch of the open group and remembers it, which silently moves the print.
    if ('fetchData' in swatches && typeof swatches.fetchData === 'function') {
      swatches.pending = swatches.fetchData({ filtering: true });
    }
  }

  /**
   * Name the print the wizard is on, so the selector marks that swatch instead of settling on
   * its own first one. Handles, because that is what the url carries — and every garment of
   * the print, since a matching set's swatch is addressed by whichever one was ranked.
   */
  #namePrintToSelector() {
    const swatches = this.querySelector('ss-print-swatches');
    if (!(swatches instanceof HTMLElement)) return;

    const handles = this.#isMatchingSet ? this.#prints.filter(Boolean) : [this.selectedPrint];
    const named = handles.filter(Boolean).join(',');

    if (named) swatches.dataset.productHandle = named;
    else delete swatches.dataset.productHandle;
  }

  /**
   * Restore `?category=&style=&size=&step=` from the URL, dropping anything the current
   * state cannot justify. #syncUrl then rewrites the URL from what applied.
   */
  #applyUrlSelection() {
    const params = this.#initialParams ?? new URLSearchParams(location.search);

    this.#restoring = true;

    this.#prints = (params.get('print') ?? '').split(',').map((handle) => handle || null);

    const named = parseInt(params.get('selection') ?? '1', 10);
    if (Number.isInteger(named) && named > 0) this.activeSelection = named - 1;

    // One slot per selection, in order. A size its style does not offer is held by the grid
    // until one does.
    const styles = (params.get('style') ?? '').split(',');
    const sizes = (params.get('size') ?? '').split(',');

    this.selections.forEach((selection, index) => {
      selection.restore(styles[index] || null, sizes[index] || null);
    });

    this.#restoring = false;

    this.#restoreCategory(params.get('category'));

    // Before the walk: the print step comes into view and the selector would answer with its
    // own first swatch, overwriting the print the url just named.
    this.#namePrintToSelector();

    // Unnamed, aim at the end: the walk stops at the first step the restored selections
    // leave unsatisfied, so this lands on whatever the url still has not answered. A PDP
    // linking in cannot name a step anyway — the handles belong to this flow, not to it.
    const requested = this.#stepFromHandle(params.get('step'));
    const target = Number.isInteger(requested) ? Math.min(Math.max(requested, 1), this.maxStep) : this.maxStep;

    while (this.currentStep < target && this.canAdvance) {
      this.goToStep(this.currentStep + 1);
    }

    // After the walk: which selection is in play depends on the step, so rendering before it
    // would dress the wrong one — and record its print over the one the url named.
    this.#renderTopPrint();
  }

  /**
   * Mirror the current selection in the URL so a step can be shared or reloaded.
   * replaceState, so Back still leaves the page rather than walking back a step.
   */
  #syncUrl() {
    const url = new URL(location.href);
    const set = (key, value) => {
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    };

    const selections = this.selections;
    const index = selections.indexOf(this.selection);
    const list = (values) => (values.some(Boolean) ? values.join(',') : '');

    // Only worth naming when there is more than one to tell apart.
    set('selection', selections.length > 1 && index >= 0 ? String(index + 1) : '');
    set('category', this.activeCategoryTile?.dataset.categoryHandle ?? '');
    set('style', list(selections.map((selection) => selection.style?.dataset.style ?? '')));
    set('size', list(selections.map((selection) => selection.size ?? '')));
    set('print', list(selections.map((_, index) => this.#prints[index] ?? '')));

    set('step', this.currentStep > 1 ? this.#stepHandle(this.currentStep) : '');

    // A comma is legal unencoded in a query value, and the separator is easier to read that
    // way than as %2C.
    const href = url.href.replace(/%2C/g, ',');

    if (href !== location.href) history.replaceState(null, '', href);
  }
}

if (!customElements.get('product-builder')) {
  customElements.define('product-builder', ProductBuilder);
}
